import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Users, Calendar, BookOpen, GraduationCap, MapPin, User, Clock } from 'lucide-react';
import useClasses from '../hooks/useClasses';
import { useSchoolName } from '../hooks/useSchools';
import { CLASS_STATUSES, homeroomTeacherLabel, normalizeSchoolYear } from '../constants';
import type { Class } from '../types';
import ClassStudentList from '../components/ClassStudentList';
import EditClassModal from '../components/EditClassModal';

const formatDate = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('vi-VN');
};

const ClassDetailPage: React.FC = () => {
  const { classId } = useParams();
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Use class detail hook
  const { useClassDetail } = useClasses();
  const { 
    data: classData, 
    isLoading, 
    isError,
    error 
  } = useClassDetail(classId || '');
  const school = useSchoolName(classData?.schoolId, classData?.school?.name);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, string> = {
      [CLASS_STATUSES.ACTIVE]: 'bg-green-100 text-green-800',
      [CLASS_STATUSES.INACTIVE]: 'bg-gray-100 text-gray-800',
      [CLASS_STATUSES.ARCHIVED]: 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status === CLASS_STATUSES.ACTIVE && 'Hoạt động'}
        {status === CLASS_STATUSES.INACTIVE && 'Không hoạt động'}
        {status === CLASS_STATUSES.ARCHIVED && 'Đã lưu trữ'}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-4 mb-2">
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="flex items-center space-x-4 mt-2">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
            <div className="h-4 w-80 bg-gray-200 rounded animate-pulse mt-1"></div>
          </div>
          <div className="flex space-x-3">
            <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* Summary Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="ml-4">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-6 w-16 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Class Info Card Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-1"></div>
                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Student List Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center space-x-4 p-4 border border-gray-100 rounded-lg">
                  <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-3 w-48 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-3 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !classData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Lỗi tải dữ liệu</h2>
          <p className="text-gray-600 mb-4">
            {error?.message || 'Không tìm thấy thông tin lớp học'}
          </p>
          <Link 
            to="/admin/class"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Quay lại danh sách
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-4 mb-2">
            <Link
              to="/admin/class"
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Quay lại danh sách
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{classData.name}</h1>
          <div className="flex items-center space-x-4 mt-2">
            <span className="text-sm text-gray-600">Mã lớp: {classData.code}</span>
            <span className="text-sm text-gray-600">•</span>
            <span className="text-sm text-gray-600">{classData.subject}</span>
            <span className="text-sm text-gray-600">•</span>
            <span className="text-sm text-gray-600">Khối {classData.grade}</span>
            <span className="text-sm text-gray-600">•</span>
            {getStatusBadge(classData.status)}
          </div>
          <p className="text-gray-600 mt-1">
            Quản lý thông tin, học sinh và tiến độ học tập của lớp học
          </p>
        </div>
        <div className="flex space-x-3">
          {/* Chưa có id (dữ liệu lớp chưa đọc được) thì không cho sửa, tránh PUT /schoolclass/undefined. */}
          <button
            type="button"
            onClick={() => setIsEditOpen(true)}
            disabled={!classData.id}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Edit className="h-4 w-4" />
            Chỉnh sửa
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 rounded-lg shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
            <Trash2 className="h-4 w-4" />
            Xóa
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng học sinh</p>
              <p className="text-2xl font-semibold text-gray-900">{classData.studentCount || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Giáo viên</p>
              <p className="text-2xl font-semibold text-gray-900">{classData.teacherCount || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <BookOpen className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Môn học</p>
              <p className="text-2xl font-semibold text-gray-900">{classData.subject || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Class Information */}
      <div className="bg-white shadow-sm border rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Thông tin Lớp học</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                Thông tin cơ bản
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Mã lớp</dt>
                  <dd className="mt-1 text-sm text-gray-900">{classData.code}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Tên lớp</dt>
                  <dd className="mt-1 text-sm text-gray-900">{classData.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Môn học</dt>
                  <dd className="mt-1 text-sm text-gray-900">{classData.subject}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Khối</dt>
                  <dd className="mt-1 text-sm text-gray-900">{classData.grade}</dd>
                </div>
              </dl>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                Thông tin bổ sung
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Năm học</dt>
                  <dd className="mt-1 text-sm text-gray-900">{normalizeSchoolYear(classData.schoolYear)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sĩ số</dt>
                  <dd className="mt-1 text-sm text-gray-900">{classData.studentCount}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Số giáo viên</dt>
                  <dd className="mt-1 text-sm text-gray-900">{classData.teacherCount}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Trạng thái</dt>
                  <dd className="mt-1">{getStatusBadge(classData.status)}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Additional Class Fields */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                Thông tin chi tiết
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Giáo viên phụ trách</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {homeroomTeacherLabel(classData.teacherId, classData.teacher?.name)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Trường</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {school.name || (school.isLoading ? 'Đang tải…' : classData.schoolId ? 'Không tìm thấy trường' : 'Chưa cập nhật')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Danh sách mời</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {classData.invites && classData.invites.length > 0 
                      ? classData.invites.join(', ') 
                      : 'Không có'
                    }
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Timestamps */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Ngày tạo</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(classData.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Cập nhật lần cuối</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(classData.updatedAt)}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Student List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <ClassStudentList 
          classId={classId || ''} 
          className={classData.name || ''}
        />
      </div>

      <EditClassModal
        classItem={isEditOpen ? classData : null}
        onClose={() => setIsEditOpen(false)}
      />
    </div>
  );
};

export default ClassDetailPage;
